import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

/**
 * Below this **container** width the frozen (sticky) first/last columns would
 * consume — and eventually cover — the whole row, hiding the middle columns, so
 * we unfreeze and let the table scroll normally. Set comfortably above the worst
 * case (Name `--title-column-width` 500 + a leading checkbox + the trailing
 * actions column ≈ 670px of sticky width) so the middle columns are never
 * covered while the columns are still frozen. Measured against the table's own
 * container, not the viewport (see `useFreezeColumns`).
 */
export const FREEZE_COLUMNS_MIN_WIDTH = 800;

/**
 * Trailing debounce for ResizeObserver-driven re-measures. A pane that animates
 * its width across the threshold (e.g. a collapsing sidebar) fires the observer
 * on every frame; without this each frame that crosses the boundary would flip
 * `frozen` and remount the table. Debouncing collapses that to a single settle.
 * The initial measurement is still synchronous (no debounce) so first paint is
 * correct.
 */
const RESIZE_DEBOUNCE_MS = 100;

// `useLayoutEffect` runs before the browser paints (so the first paint already
// reflects the measured freeze state without a visible flash), but it warns
// during SSR. Fall back to `useEffect` on the server where it's a no-op anyway.
const useIsomorphicLayoutEffect =
  globalThis.window === undefined ? useEffect : useLayoutEffect;

/**
 * Find the actually-scrolling descendant of the table container (the DS's
 * `ResizableTableContainer`, whose hashed class we can't rely on). Mirrors the
 * DS's own scroll-container detection: the first descendant that overflows
 * horizontally with an `auto`/`scroll` overflow-x.
 */
function findScrollContainer(root: HTMLElement): HTMLElement | undefined {
  for (const element of root.querySelectorAll<HTMLElement>("*")) {
    if (element.scrollWidth > element.clientWidth) {
      const overflowX = getComputedStyle(element).overflowX;
      if (overflowX === "auto" || overflowX === "scroll") {
        return element;
      }
    }
  }
  return undefined;
}

interface PreservedState {
  scrollLeft: number;
  scrollTop: number;
  /** Focus was inside the table when the freeze state flipped. */
  hadFocus: boolean;
}

/**
 * Returns whether the table's sticky first/last columns should be **frozen**
 * (horizontally pinned), based on the table **container** width. Below
 * {@link FREEZE_COLUMNS_MIN_WIDTH} it returns `false` so the table reflows to
 * plain horizontal scrolling (a WCAG 1.4.10-conformant pattern) instead of the
 * frozen columns covering the middle columns.
 *
 * Consumers pass the result to the DS `isSticky` column prop **and** to a `key`
 * on the `DataTable`. The DS `BetaDataTable` snapshots `isSticky` at mount and
 * never resets its internal sticky map when a column stops being sticky, so
 * toggling the prop alone cannot release existing sticky styling — remounting
 * via a changing `key` is the only public-API way to fully clear it. (Tracked
 * upstream; remove the remount once the DS supports a responsive unfreeze.)
 *
 * The remount discards the DS scroll position and moves focus out of the table.
 * To soften that, we capture the scroll offset + whether focus was inside the
 * table just before each flip and restore them once the new tree has mounted,
 * so a resize across the threshold doesn't jump the user to the top or drop
 * their keyboard focus onto `<body>`.
 *
 * Measuring the container rather than the viewport keeps a table in a narrow
 * pane/sidebar correct even when the viewport itself is wide. SSR-safe: defaults
 * to frozen (desktop-first) and runs in a layout effect so the first client
 * paint never flashes the wrong state.
 */
export function useFreezeColumns(
  reference: RefObject<HTMLElement | null>,
): boolean {
  const [frozen, setFrozen] = useState(true);
  // Mirror of `frozen` so the observer callback can detect a real transition
  // without re-subscribing, and capture state only when the value actually flips.
  const frozenReference = useRef(frozen);
  frozenReference.current = frozen;
  // Scroll/focus captured just before a flip, consumed by the restore effect
  // once the remounted tree has committed.
  const preservedReference = useRef<PreservedState | undefined>(undefined);

  useIsomorphicLayoutEffect(() => {
    const element = reference.current;
    // Guard for environments without ResizeObserver / a mounted element
    // (jsdom/SSR): stay frozen by default.
    if (!element || typeof ResizeObserver !== "function") {
      return;
    }

    const measure = () => {
      const next = element.clientWidth >= FREEZE_COLUMNS_MIN_WIDTH;
      if (next === frozenReference.current) {
        return;
      }
      // Capture before the flip: the flip changes the DataTable `key`, which
      // remounts it and resets the DS scroll container + moves focus to <body>.
      const scroller = findScrollContainer(element);
      preservedReference.current = {
        scrollLeft: scroller?.scrollLeft ?? 0,
        scrollTop: scroller?.scrollTop ?? 0,
        hadFocus: element.contains(document.activeElement),
      };
      setFrozen(next);
    };

    // Initial measurement is synchronous so first paint is correct; only
    // observer-driven re-measures are debounced.
    measure();

    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(measure, RESIZE_DEBOUNCE_MS);
    });
    observer.observe(element);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [reference]);

  // Restore scroll + focus after the flip-driven remount has committed.
  useIsomorphicLayoutEffect(() => {
    const preserved = preservedReference.current;
    if (!preserved) {
      return;
    }
    preservedReference.current = undefined;

    const element = reference.current;
    if (!element) {
      return;
    }

    const scroller = findScrollContainer(element);
    if (scroller) {
      scroller.scrollLeft = preserved.scrollLeft;
      scroller.scrollTop = preserved.scrollTop;
    }

    // If focus was inside the table and the remount dropped it (to <body>),
    // return it to the table region so keyboard users don't lose their place.
    if (preserved.hadFocus && !element.contains(document.activeElement)) {
      element.tabIndex = -1;
      element.focus({ preventScroll: true });
    }
  }, [frozen, reference]);

  return frozen;
}
